import axiosInstance from './axiosInstance';

const REPORTS_BASE_URL = '/Corrohealth/api/reports';

// Report Definition APIs
export const createReport = async (reportData) => {
  const response = await axiosInstance.post(REPORTS_BASE_URL, reportData);
  return response.data;
};

export const updateReport = async (reportId, reportData) => {
  const response = await axiosInstance.put(`${REPORTS_BASE_URL}/${reportId}`, reportData);
  return response.data;
};

export const deleteReport = async (reportId) => {
  const response = await axiosInstance.delete(`${REPORTS_BASE_URL}/${reportId}`);
  return response.data;
};

export const getReportById = async (reportId) => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}/${reportId}`);
  return response.data;
};

export const getMyReports = async () => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}?filter=my`);
  return response.data.reports || [];
};

export const getPublicReports = async () => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}?filter=public`);
  return response.data.reports || [];
};

export const getAccessibleReports = async () => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}`);
  return response.data.reports || [];
};

export const getReportTemplates = async () => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}?filter=templates`);
  return response.data.reports || [];
};

export const cloneReport = async (reportId, newName) => {
  const response = await axiosInstance.post(`${REPORTS_BASE_URL}/${reportId}/clone`, { newName });
  return response.data;
};

export const toggleFavorite = async (reportId, isFavorite) => {
  const response = await axiosInstance.post(`${REPORTS_BASE_URL}/${reportId}/favorite`, { isFavorite });
  return response.data;
};

export const togglePublic = async (reportId, isPublic) => {
  const response = await axiosInstance.post(`${REPORTS_BASE_URL}/${reportId}/public`, { isPublic });
  return response.data;
};

// Report Execution APIs
export const executeReport = async (reportId, parameters = {}) => {
  const response = await axiosInstance.post(`${REPORTS_BASE_URL}/${reportId}/execute`, { parameters });
  return response.data;
};

export const executeReportAsync = async (reportId, parameters = {}) => {
  const response = await axiosInstance.post(`${REPORTS_BASE_URL}/${reportId}/execute-async`, { parameters });
  return response.data;
};

export const getExecutionStatus = async (executionId) => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}/executions/${executionId}/status`);
  return response.data;
};

export const getExecutionResult = async (executionId) => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}/executions/${executionId}/result`);
  return response.data;
};

export const getExecutionHistory = async (reportId) => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}/${reportId}/execution-history`);
  return response.data;
};

// Report Export APIs
export const exportReport = async (reportId, format, parameters = {}) => {
  const response = await axiosInstance.post(
    `${REPORTS_BASE_URL}/${reportId}/export`,
    { format, parameters },
    { responseType: 'blob' }
  );
  return response;
};

export const downloadExport = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Field Metadata APIs
export const getFieldsForDataSource = async (dataSource) => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}/fields/${dataSource}`);
  return response.data.fields || [];
};

export const getDataSources = async () => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}/data-sources`);
  return response.data.dataSources || [];
};

// Report Sharing APIs
export const shareWithUser = async (reportId, userId, permission) => {
  const response = await axiosInstance.post(`${REPORTS_BASE_URL}/${reportId}/share/user`, { userId, permission });
  return response.data;
};

export const shareWithRole = async (reportId, roleId, permission) => {
  const response = await axiosInstance.post(`${REPORTS_BASE_URL}/${reportId}/share/role`, { roleId, permission });
  return response.data;
};

export const shareWithLocation = async (reportId, location, permission) => {
  const response = await axiosInstance.post(`${REPORTS_BASE_URL}/${reportId}/share/location`, { location, permission });
  return response.data;
};

export const removeShare = async (reportId, shareId) => {
  const response = await axiosInstance.delete(`${REPORTS_BASE_URL}/${reportId}/share/${shareId}`);
  return response.data;
};

export const getReportShares = async (reportId) => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}/${reportId}/shares`);
  return response.data;
};

// Report Scheduling APIs
export const scheduleReport = async (reportId, cronExpression, emailRecipients) => {
  const response = await axiosInstance.post(`${REPORTS_BASE_URL}/${reportId}/schedule`, { cronExpression, emailRecipients });
  return response.data;
};

export const updateSchedule = async (reportId, cronExpression, emailRecipients) => {
  const response = await axiosInstance.put(`${REPORTS_BASE_URL}/${reportId}/schedule`, { cronExpression, emailRecipients });
  return response.data;
};

export const disableSchedule = async (reportId) => {
  const response = await axiosInstance.delete(`${REPORTS_BASE_URL}/${reportId}/schedule`);
  return response.data;
};

export const getScheduledReports = async () => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}/scheduled`);
  return response.data;
};

// Report Statistics
export const getReportStatistics = async (reportId) => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}/${reportId}/statistics`);
  return response.data;
};

// Search and Filter
export const searchReports = async (searchTerm) => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}/search`, { params: { q: searchTerm } });
  return response.data;
};

export const getReportsByCategory = async (category) => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}?category=${category}`);
  return response.data.reports || [];
};

export const getReportsByDataSource = async (dataSource) => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}?dataSource=${dataSource}`);
  return response.data.reports || [];
};

export const getFavoriteReports = async () => {
  const response = await axiosInstance.get(`${REPORTS_BASE_URL}?filter=favorites`);
  return response.data.reports || [];
};

// Validate cron expression
export const validateCronExpression = async (cronExpression) => {
  const response = await axiosInstance.post(`${REPORTS_BASE_URL}/validate-cron`, { cronExpression });
  return response.data;
};

// Preview SQL Query
export const previewQuery = async (dataSource, configuration, parameters = {}) => {
  const response = await axiosInstance.post(`${REPORTS_BASE_URL}/preview-query`, {
    dataSource,
    configuration,
    parameters
  });
  return response.data;
};

// Validate Custom SQL Query (Advanced Mode)
export const validateSqlQuery = async (query) => {
  const response = await axiosInstance.post(`${REPORTS_BASE_URL}/validate-sql`, { query });
  return response.data;
};
